import { Loader, ModalComponentWrapper } from '@/shared/components';
import type { AccountCreateButtonType } from '@/shared/types';
import { OpenAccountButton } from './OpenAccountButton';
import { useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OpenAccountForm } from '@/features/account-open/ui/OpenAccountForm';
import { type OpenAccountType } from '@/features/account-open/model/types';
import InvestAccountIcon from '@/shared/assets/invest-account-icon.svg?react';
import SpotAccountIcon from '@/shared/assets/spot-account-icon.svg?react';
import PercentIcon from '@/shared/assets/percent-icon.svg?react';
import EarnAccountIcon from '@/shared/assets/earn-account-icon.svg?react';
import LogoSmallIcon from '@/shared/assets/f2ex-logo-small.svg?react';
import { useAccountOpeningTimer } from '@/features/account-open/model';
import type { OpenAccountResponseDto } from '@/features/account-open/api/types';
import { useOpenAccountAvailabilityStore } from '@/features/account-open/model/useOpenAccountAvailabilityStore';
import { useCreateAccountsSettingsDictionary } from '@/shared/api/queries';
import type { AccountType } from '@/entities/account';
import { useModalStore } from '@/shared/lib';
import { useAccountCreateModal } from '../model';

type Props = {
  onSuccess: (
    data: OpenAccountResponseDto,
    accountType: OpenAccountType,
    assetCode?: string
  ) => void;
  type?: AccountType;
};

const OpenAccountModalContent = ({ onSuccess, type }: Props) => {
  const { t } = useTranslation();
  const { showDialogComponent } = useModalStore();
  const { pendingAccounts } = useAccountOpeningTimer();
  const { createAccountsSettingsData, createAccountsSettingsLoading } =
    useCreateAccountsSettingsDictionary();
  const {
    openSpotIsAvailable,
    openInvestIsAvailable,
    openMarginIsAvailable,
    openEarnIsAvailable,
    spotCreateIsActive,
    investCreateIsActive,
    marginCreateIsActive,
    earnCreateIsActive,
  } = useOpenAccountAvailabilityStore();

  const { openCreateAccountModal } = useAccountCreateModal();

  const accountsButtonsByType: Array<AccountCreateButtonType> = useMemo(() => {
    const buttons: Array<AccountCreateButtonType> = [
      {
        type: 'spot',
        title: t('personalAccount.accountsSection.spotTitle'),
        description: t('openAccount.spotDescription'),
        icon: SpotAccountIcon,
        label: LogoSmallIcon,
        isPending: pendingAccounts.some(
          account => account.accountType === 'spot'
        ),
        isAvailable: openSpotIsAvailable,
        isCreateActive: spotCreateIsActive,
      },
      {
        type: 'margin',
        title: t('personalAccount.accountsSection.leverageTitle'),
        description: t('openAccount.marginDescription'),
        icon: PercentIcon,
        label: LogoSmallIcon,
        isPending: pendingAccounts.some(
          account => account.accountType === 'margin'
        ),
        isAvailable: openMarginIsAvailable,
        isCreateActive: marginCreateIsActive,
      },
      {
        type: 'invest',
        title: t('personalAccount.accountsSection.investTitle'),
        description: t('openAccount.investDescription'),
        icon: InvestAccountIcon,
        label: LogoSmallIcon,
        isPending: pendingAccounts.some(
          account => account.accountType === 'invest'
        ),
        isAvailable: openInvestIsAvailable,
        isCreateActive: investCreateIsActive,
      },
      {
        type: 'earn',
        title: 'Earn',
        description: t('openAccount.earnDescription'),
        icon: EarnAccountIcon,
        label: LogoSmallIcon,
        isPending: pendingAccounts.some(
          account => account.accountType === 'earn'
        ),
        isAvailable: openEarnIsAvailable,
        isCreateActive: earnCreateIsActive,
      },
    ];

    return buttons.filter(account => account.isCreateActive);
  }, [
    t,
    pendingAccounts,
    openInvestIsAvailable,
    openSpotIsAvailable,
    openMarginIsAvailable,
    openEarnIsAvailable,
    spotCreateIsActive,
    investCreateIsActive,
    marginCreateIsActive,
    earnCreateIsActive,
  ]);

  const openAccountForm = (accountType: OpenAccountType) => {
    showDialogComponent({
      component: (
        <OpenAccountForm
          accountType={accountType}
          createAccountsSettings={createAccountsSettingsData?.value}
          onSuccess={onSuccess}
          onBack={() => openCreateAccountModal()}
        />
      ),
    });
  };

  const createIsActiveByType: Record<OpenAccountType, boolean> = {
    spot: spotCreateIsActive,
    margin: marginCreateIsActive,
    invest: investCreateIsActive,
    earn: earnCreateIsActive,
  };

  const preselectedAccountType: OpenAccountType | null =
    type && type !== 'ewallet' ? (type === 'leverage' ? 'margin' : type) : null;
  const shouldOpenPreselectedForm =
    preselectedAccountType !== null &&
    createIsActiveByType[preselectedAccountType];

  useLayoutEffect(() => {
    if (
      shouldOpenPreselectedForm &&
      preselectedAccountType &&
      createAccountsSettingsData?.value
    ) {
      openAccountForm(preselectedAccountType);
    }
  }, [
    shouldOpenPreselectedForm,
    preselectedAccountType,
    createAccountsSettingsData,
  ]);

  return createAccountsSettingsLoading ? (
    <Loader />
  ) : (
    <ModalComponentWrapper
      title={t('openAccount.title')}
      headerClassName="text-secondary-bg bg-secondary-bg/50">
      <div className="flex flex-col gap-[1.6rem]">
        {accountsButtonsByType.map(account => {
          return (
            <OpenAccountButton
              key={account.type}
              account={account}
              onClick={() => {
                openAccountForm(account.type);
              }}
            />
          );
        })}
      </div>
    </ModalComponentWrapper>
  );
};

export { OpenAccountModalContent };
